/* import __COLOCATED_TEMPLATE__ from './playground-heading.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */

import { startSurvey } from 'embercom/lib/intercom-widget-helper';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { registerDestructor } from '@ember/destroyable';
import type IntlService from 'embercom/services/intl';
import type RouterService from '@ember/routing/router-service';
import type CsvService from 'embercom/services/csv';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import type PlaygroundApi from 'embercom/services/playground-api';
import type { GroupSummary } from 'embercom/lib/fin-playground/group-summary';
import moment from 'moment-timezone';

interface PlaygroundHeadingArgs {
  showExceedingQuestionLimitBanner: boolean;
  updateShowExceedingQuestionLimitBanner: (showExceedingQuestionLimitBanner: boolean) => void;
  addQuestionDropdownOptions: Array<{
    items: Array<{
      text: string;
      value?: string;
      icon?: string;
      isDisabled?: boolean;
      onSelectItem?: () => void;
    }>;
  }>;
  selectAddQuestionDropdownOption: (option: string) => void;
  openNewGroupModal: (place: string) => void;
  manageGroupsDropdownOptions: Array<{
    items: Array<{
      text: string;
      value: string;
      onSelectItem: () => void;
    }>;
  }>;
}

const SURVEY_ID = 45346448;

export default class PlaygroundHeading extends Component<PlaygroundHeadingArgs> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare csv: CsvService;
  @service declare intercomEventService: $TSFixMe;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare playgroundApi: PlaygroundApi;
  @service declare store: $TSFixMe;

  @tracked private lastRefreshTime = Date.now();
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(owner: unknown, args: PlaygroundHeadingArgs) {
    super(owner, args);

    registerDestructor(this, () => {
      this.stopTimer();
    });
  }

  private startTimer() {
    if (this.timer) {
      return; // Already running
    }

    this.timer = setInterval(() => {
      this.lastRefreshTime = Date.now();
    }, 60000);
  }

  private stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  get groupSummaries() {
    return this.playgroundApi.playgroundSummary?.groupSummaries;
  }

  get playground() {
    return this.playgroundApi.playground;
  }

  get canUseFinPlaygroundShowLastModified() {
    return this.appService.app.canUseFinPlaygroundShowLastModified;
  }

  get isQuestionGenerationRunningForApp(): boolean {
    return this.playground?.questionGeneration.isJobRunningForApp ?? false;
  }

  get hasQuestions() {
    return this.playgroundApi.playground?.hasQuestions;
  }

  get addQuestionDropdownOptions() {
    let items = [];

    if (this.playground?.questionGeneration.isPossible) {
      items.push({
        text: this.intl.t('ai-agent.playground.add-questions-dropdown.generate-questions'),
        value: 'generate-questions',
        isDisabled: this.isQuestionGenerationRunningForApp,
      });
    }

    items.push(
      {
        text: this.intl.t('ai-agent.playground.add-questions-dropdown.upload-csv'),
        value: 'upload-csv',
      },
      {
        text: this.hasQuestions
          ? this.intl.t('ai-agent.playground.add-questions-dropdown.add-questions-manually')
          : this.intl.t('ai-agent.playground.add-question'),
        value: 'add-question',
      },
    );

    return [{ items }];
  }

  get isAddQuestionDropdownDisabled() {
    return (
      this.playgroundApi.isPlaygroundLocked ||
      this.playgroundApi.playgroundIsLoading ||
      this.playground?.hasReachedQuestionLimit
    );
  }

  get runTestButtonLabel(): string {
    return this.playground?.isRunning
      ? this.intl.t('ai-agent.playground.running-test')
      : this.intl.t('ai-agent.playground.run-test');
  }

  get deductionRequiredToAddQuestion(): number {
    if (!this.playground?.hasReachedQuestionLimit) {
      return 0;
    }

    return 1 + this.playground?.questions.length - this.playground?.questionLimit;
  }

  get groupSummaryItems() {
    if (!this.groupSummaries) {
      return;
    }

    return [
      {
        items: this.groupSummaries.map((groupSummary) => ({
          text: groupSummary.name,
          value: groupSummary.id,
        })),
      },
    ];
  }

  get currentGroupLastModified() {
    let currentGroup = this.groupSummaries?.find(
      (groupSummary) => groupSummary.id === this.playground?.id,
    );

    if (!currentGroup) {
      this.stopTimer(); // No timestamps to update
      return null;
    }

    // Start timer only when timestamps are displayed
    this.startTimer();

    return this.formatLastModified(currentGroup);
  }

  get currentGroupAdmin() {
    let currentGroup = this.groupSummaries?.find(
      (groupSummary) => groupSummary.id === this.playground?.id,
    );

    if (!currentGroup?.lastModifiedByAdminId) {
      return null;
    }

    return this.store.peekRecord('admin', currentGroup.lastModifiedByAdminId);
  }

  get currentGroupAdminName() {
    let admin = this.currentGroupAdmin;
    return admin?.name || null;
  }

  formatLastModified(groupSummary: GroupSummary) {
    if (!groupSummary.lastModifiedAt || !groupSummary.lastModifiedByAdminId) {
      return null;
    }

    let timeAgo = this.formatTimeFromNow(groupSummary.lastModifiedAt);

    return this.intl.t('ai-agent.playground.updated-by-prefix', {
      timeAgo,
    });
  }

  formatTimeFromNow(timestamp: string) {
    /*
      Format timestamp with locale support using auto-tracking.
      The getter will recompute when this.intl.primaryLocale or lastRefreshTime changes.
     */
    let locale = this.intl.primaryLocale;
    // Reference lastRefreshTime to trigger updates
    this.lastRefreshTime;
    return moment(timestamp).locale(locale).fromNow();
  }

  get isIdInGroupSummaries() {
    return this.groupSummaries?.some((groupSummary) => groupSummary.id === this.playground?.id);
  }

  get footerActions() {
    return [
      {
        text: this.intl.t('ai-agent.playground.header-buttons.create-new-test'),
        icon: 'new',
        action: () => this.args.openNewGroupModal('playground_header'),
      },
    ];
  }

  @action onSelectItem(groupId: number) {
    if (groupId === this.playground?.id) {
      return;
    } else {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        object: 'group_dropdown_item',
        context: 'playground',
      });

      this.router.transitionTo(this.router.currentRouteName, groupId);
    }
  }

  @action
  startSurvey() {
    startSurvey(SURVEY_ID);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::PlaygroundHeading': typeof PlaygroundHeading;
    'ai-agent/playground/playground-heading': typeof PlaygroundHeading;
  }
}
